import { UseFormSetError } from 'react-hook-form';
import { ApisError } from './ApisForm.constants';
import { FormContactData, ModelContactData } from './components/ContactData';
import {
	FormDocumentationRequired,
	ModelDocumentationRequired,
	RequiredField,
	RequiredFieldOptions,
	RequiredFieldTypesOrigin
} from './components/DocumentationRequired/DocumentationRequired.model';
import { FormPassengerAddress, ModelPassengerAddress } from './components/PassengerAddress/PassengerAddress.model';
import {
	FormPersonalData,
	ModelPersonalData,
	TypePassengerAdultChild,
	TypePassengerInfant
} from './components/PersonalData/PersonalData.model';
import { TFunction } from 'i18next';

export type HandleSubmitCallback = (passenger: FormDataApis) => Promise<ResolveType>;
export type ResolveType = {
	status: boolean;
	message?: string;
	passenger?: FormDataApis;
};
export type RejectType = {
	status: boolean;
	message?: string;
	errors?: ApisError[];
};
export type ApisFormProps = {
	/**
	 * List nationalities
	 * @type listCountries
	 */
	listNationalities: ItemList[];
	/**
	 * List countries
	 * @type listCountries
	 */
	listCountries: ItemList[];
	/**
	 * List states
	 * @type listStates
	 */

	listStates: ItemList[];
	/**
	 * Prefix
	 * @type countriesCodes
	 */
	countryCodes: ItemList[];
	/**
	 * market
	 * @type listCountries
	 */
	market: string;
	/**
	 * Model info make form
	 */
	model: ModelApisForm;
	/**
	 * Function handle submit form
	 */
	onSubmit: HandleSubmitCallback;
	/**
	 * Is disable form
	 * @type boolean
	 */
	isBlockForm?: boolean;
	/**
	 * Is multiple passengers
	 * @type boolean
	 */
	isMultiPassengers: boolean;
	/**
	 * Id component
	 * @type string
	 */
	id?: string;
};

export type ModelApisForm = {
	id: number;
	typePassenger: TypePassengerAdultChild;
	personalData: ModelPersonalData;
	contactData: ModelContactData;
	passengerAddress?: ModelPassengerAddress;
	documentationRequired: ModelDocumentationRequired;
	infant?: ModelApisInfant;
	isPrivacyStatement?: boolean;
	gdpr?: ModelGdpr;
	bookingInfoPassenger: BookingInfoPassenger;
};

export type ErrorApisObject = {
	code: ApisErrorType;
	description?: string;
};

export type ApisErrorType =
	| 'TM_VISA_REQUIRED'
	| 'TM_ESTA_REQUIRED'
	| 'TM_VISA_OPTIONAL'
	| 'TM_RETURN_TICKET_REQUIRED'
	| 'TM_TRAVEL_DOC_REQUIRED'
	| 'TM_PSPT_VALIDITY'
	| 'PSPT_VALIDITY_3_MONTHS'
	| 'PSPT_VALIDITY_6_MONTHS'
	| 'API_DOCUMENT_EXPIRY_DATE';

export type ModelApisInfant = {
	typePassenger: TypePassengerInfant;
	personalData: ModelPersonalData;
	documentationRequired: ModelDocumentationRequired;
};
export type ModelGdpr = {
	text: string;
	url: string;
};

export type ListStates = ItemList[];
export type SelectOptions = {
	value: string;
	label: string;
};

export type ItemList = {
	code: string;
	description: string;
	normalizedCode?: string;
	isoCode?: string;
	countryCode?: string;
};

export type FormDataApis = {
	personalData: FormPersonalData;
	contactData: FormContactData;
	passengerAddress?: FormPassengerAddress;
	documentationRequired: FormDocumentationRequired;
	infant?: FormDataApisInfant;
	checkbox: FormCheckBoxInfo;
};

export type FormCheckBoxInfo = {
	notSendNewsletter?: boolean;
	usPrivacyEmergencyData?: boolean;
	confirmEmergencyData: boolean;
};

export type FormDataApisInfant = {
	personalData: FormPersonalData;
	contactData?: FormContactData;
	passengerAddress?: FormPassengerAddress;
	documentationRequired: FormDocumentationRequired;
};

export type ListOptions = {
	countryCode?: SelectOptions[];
	nationalities?: SelectOptions[];
	countries?: SelectOptions[];
	states?: SelectOptions[];
	mandatoryDocsTypes?: SelectOptions[];
	visaDocsTypes?: SelectOptions;
	otherDocsTypes?: SelectOptions[];
};

export type ApisFormContextType = {
	listOptions: ListOptions;
	documentationRequired: ModelDocumentationRequired;
	passengerAddress?: ModelPassengerAddress;
	infant?: ModelApisInfant;
	isFormDisabled?: boolean;
	isPrivacyStatement?: boolean;
	apisErrors?: ErrorApisObject[];
	gdpr?: ModelGdpr;
	id?: number;
	bookingInfoPassenger: BookingInfoPassenger;
	contactData?: ModelContactData;
	isMultiPassengers?: boolean;
};

export type DocumentObject = {
	type: SelectOptions | ItemList;
	expiryDate?: string;
	number?: string;
	country?: SelectOptions;
	requiredFields?: RequiredFieldTypesOrigin[];
	requiredFieldsOptions?: RequiredFieldOptions[];
};
export const initRequiredFields: RequiredField = {
	number: true,
	expiryDate: true,
	issuerCountry: true
};
export const resetRequiredFields: RequiredField = {
	number: false,
	expiryDate: false,
	issuerCountry: false
};

export type OptionCheckbox = {
	name: 'checkbox.notSendNewsletter' | 'checkbox.usPrivacyEmergencyData' | 'checkbox.confirmEmergencyData';
	label: 'NOT_SEND_NEWSLETTER' | 'CHECK_US_PRIVACY_ACT_STATEMENT' | 'CHECK_CONFIRM_INFORMATION_PROVIDED';
	className: string;
	required: boolean;
	i18nTrans?: boolean;
};

export type LiteralsDialog =
	| 'MODAL_US_PRIVACY_ACT_STATEMENT_BODY_P1'
	| 'MODAL_US_PRIVACY_ACT_STATEMENT_BODY_P2'
	| 'MODAL_US_PRIVACY_ACT_STATEMENT_BODY_P3';

export type LabelCheckBox =
	| 'NOT_SEND_NEWSLETTER'
	| 'CHECK_US_PRIVACY_ACT_STATEMENT'
	| 'CHECK_CONFIRM_INFORMATION_PROVIDED';

export type ControlData = {
	value: string;
	required: boolean;
	disabled?: boolean;
};

export type ErrorHandler = (setError: UseFormSetError<FormDataApis>, t: TFunction<['common', 'apis-error']>) => void;

export type BookingInfoPassenger = {
	id?: string | number;
	name: string;
	surname: string;
	infant?: {
		name: string;
		surname: string;
	};
};

export type FullName = {
	bookingInfoPassenger: BookingInfoPassenger;
	existInfant?: boolean;
};

export type ResolveFunction = (value: ResolveType | PromiseLike<ResolveType>) => void;
export type RejectFunction = (reason?: unknown) => void;
export type SimulateUpdateModelProps = {
	handleClick: () => void;
};

export const REGEXP_CLEAN = /^(?!.*[¡!?¿<>\\ºª|/·@#$%&,;=()_+{}\\[\]"^€£])/;
